/**
 * useAnalytics — centralised PostHog event tracking helper
 *
 * One place to define every business event we care about. Use this everywhere
 * instead of calling `posthog.capture()` directly so events stay consistent
 * and TypeScript catches typos.
 *
 * Usage:
 *   const { track } = useAnalytics();
 *   track('estimate_created', { line_item_count: 12, total_value: 4500 });
 *
 * Adding a new event:
 *   1. Add the event name + property shape to `AnalyticsEvents` below
 *   2. Call `track('your_new_event', { ...props })` from the relevant component
 *   3. Build a chart for it in PostHog
 *
 * Notes:
 *   - PostHog may not be initialised in local dev — every call is wrapped in
 *     a try/catch so a missing PostHog never breaks the app.
 *   - Don't put PII (raw emails, names, addresses) into event properties —
 *     that data already lives on the identified user via posthog.identify().
 *     Use IDs instead.
 *   - Don't put financial data (project_value, cost, total) into property
 *     names that match the denylist in monitoring.ts.
 */

import posthog from 'posthog-js';
import { useEntityCelebration, type EntityKind } from '@/composables/useEntityCelebration';

// Map analytics events to milestone celebrations. When a "first_X" event is
// tracked anywhere in the app, the corresponding celebration fires
// automatically — no extra wiring required at each call site.
const FIRST_EVENT_TO_MILESTONE: Partial<Record<string, EntityKind>> = {
  first_project_created: 'project',
  first_estimate_created: 'estimate',
  first_team_member_invited: 'team_member',
  first_document_uploaded: 'document',
  first_takeoff_completed: 'takeoff',
  first_customer_added: 'customer',
};

/**
 * Every business event we track. Add new ones here as the product grows.
 * Property shapes are typed so calls fail at compile time if you pass the
 * wrong properties.
 */
export interface AnalyticsEvents {
  // ── Authentication & onboarding ──────────────────────────────────────────
  signup_started: { source?: string };
  /**
   * user_id is optional because one caller genuinely does not have it: the
   * free-plan branch of /paymentstripe returns url, free_plan, plan_name and
   * message, and no id. Send it when it is known and leave it off when it is
   * not — a placeholder would give every free signup the same id, which reads
   * as one very busy user rather than as a missing field.
   */
  signup_completed: { user_id?: string | number; signup_method?: 'email' | 'google' | 'oauth' };
  /** Fired each time a registration step becomes visible. Gives per-step drop-off. */
  signup_step_viewed: { step: number; step_name: string };
  /** Server-side validation rejected a step. `fields` is field NAMES only, never values. */
  signup_validation_failed: { step: number; fields: string };
  login_succeeded: { user_id: string | number };
  login_failed: { reason?: string };
  logout: { session_duration_ms?: number };
  password_reset_requested: Record<string, never>;

  // ── Activation milestones (the big ones for the activation funnel) ───────
  first_project_created: { project_id: string | number };
  first_estimate_created: { estimate_id: string | number };
  first_team_member_invited: { invite_method?: 'email' | 'link' };
  first_document_uploaded: { document_type?: string };
  first_takeoff_completed: { project_id: string | number };
  first_customer_added: { customer_id: string | number };
  first_equipment_added: { equipment_id: string | number };
  first_supplier_added: { supplier_id: string | number };
  first_schedule_created: { project_id?: string | number };

  // ── Project management ──────────────────────────────────────────────────
  project_created: { project_id: string | number; project_type?: string };
  project_archived: { project_id: string | number };
  project_completed: { project_id: string | number };
  customer_created: { customer_id: string | number };
  equipment_created: { equipment_id: string | number; equipment_type?: string };
  supplier_created: { supplier_id: string | number };

  // ── Estimating & costing ─────────────────────────────────────────────────
  estimate_created: { estimate_id: string | number; line_item_count?: number };
  estimate_approved: { estimate_id: string | number };
  estimate_pdf_exported: { estimate_id: string | number };
  costing_added: { project_id: string | number; category?: string };
  takeoff_started: { project_id: string | number };
  takeoff_completed: { project_id: string | number; pdf_pages?: number };
  assembly_used: { assembly_name?: string };

  // ── Scheduling ──────────────────────────────────────────────────────────
  schedule_event_created: { project_id?: string | number };
  schedule_event_updated: { project_id?: string | number };
  schedule_drag_dropped: Record<string, never>;

  // ── Documents & files ────────────────────────────────────────────────────
  document_uploaded: { document_type?: string; size_kb?: number };
  document_downloaded: { document_id?: string | number };
  cloud_sync_connected: { provider: 'google_drive' | 'dropbox' | 'onedrive' | 'box' };

  // ── Team & collaboration ────────────────────────────────────────────────
  team_member_invited: { invite_method?: 'email' | 'link' };
  subcontractor_invited: Record<string, never>;
  client_invited: Record<string, never>;

  // ── Time tracking ───────────────────────────────────────────────────────
  timesheet_logged: { hours_type?: 'ordinary' | 'overtime' };
  timesheet_approved: { count?: number; report_type?: string };
  /** Which control model the builder picked for reviewing payroll. */
  payroll_review_mode_set: { mode: 'built_simple' | 'xero' };

  // ── Calculators (feeds the SEO acquisition strategy) ─────────────────────
  calculator_opened: { calculator_name: string };
  calculator_used: { calculator_name: string };

  // ── Pricing & subscriptions ─────────────────────────────────────────────
  pricing_page_viewed: { source?: string };
  plan_selected: { tier_name: string };
  checkout_started: { tier_name: string };
  checkout_completed: { tier_name: string };
  checkout_abandoned: { tier_name?: string; step?: string };
  subscription_upgraded: { from_tier: string; to_tier: string };
  subscription_downgraded: { from_tier: string; to_tier: string };
  subscription_cancelled: { tier: string; reason?: string };

  // ── Integrations ────────────────────────────────────────────────────────
  xero_connected: { provider?: string };
  /** A push of records to Xero. `kind` says what; the counts say how it went. */
  xero_synced: {
    kind?: 'timesheets' | 'leave';
    source?: string;
    pushed?: number;
    skipped?: number;
    errored?: number;
    record_count?: number;
  };
  integration_connect_started: { provider: string };
  integration_connect_failed: { provider: string; reason?: string };

  // ── Bulk import ─────────────────────────────────────────────────────────
  bulk_import_started: { entity_type: string };
  bulk_import_completed: { entity_type: string; row_count?: number; success_count?: number; error_count?: number };
  bulk_import_failed: { entity_type: string; reason?: string };

  // ── Mobile (sent from React Native to keep events consistent) ────────────
  mobile_app_opened: Record<string, never>;
  mobile_offline_sync_completed: { items_synced?: number };

  // ── Feature engagement ──────────────────────────────────────────────────
  feature_used: { feature_name: string };
  upgrade_prompt_shown: { feature_name: string; current_tier: string };
  upgrade_prompt_clicked: { feature_name: string; current_tier: string };

  // ── Help Center & onboarding tours ──────────────────────────────────────
  help_drawer_opened: { route?: string };
  help_article_viewed: { article_slug: string; category?: string };
  help_search_performed: { query_length: number; results_count: number };
  tour_started: { tour_id: string | number; tour_name: string; route?: string };
  tour_completed: { tour_id: string | number; tour_name: string; steps_total?: number; duration_ms?: number };
  tour_dismissed: { tour_id: string | number; tour_name: string };
  onboarding_step_completed: { step_id: string; step_name?: string };

  // ── Money spine: setup health (WS1) ──────────────────────────────────────
  // NB (WS10): money-spine events NEVER carry raw dollar amounts — only bands,
  // buckets, ratios, counts and booleans (privacy + better cohorting). IDs are
  // fine (they're not PII once identify() has run).
  spine_health_viewed: { status: 'ok' | 'action' | 'blocked'; failing_keys: string[]; context: 'settings' | 'cost_inbox' | 'job' };
  spine_health_fix_clicked: { key: string; context?: string };
  /** Novice guidance: the one "do this next" for a job (staged by job state). */
  next_step_shown: { step: string; estimate_id?: string | number };
  next_step_clicked: { step: string; estimate_id?: string | number };
  spine_check_resolved: { key: string };
  cost_inbox_empty_shown: { reason: 'blocked' | 'no_bills' | 'not_connected' };

  // ── Money spine: costing lifecycle ────────────────────────────────────────
  proposal_sent: { estimate_id: string | number; mode?: 'detailed' | 'simple' };
  quote_locked: { estimate_id: string | number; line_count?: number };
  quote_unlock_blocked: { estimate_id: string | number; reason?: string };
  variation_approved: { estimate_id: string | number; variation_count?: number };
  bill_assigned: { estimate_id: string | number; source: 'xero' | 'mobile' | 'manual'; auto: boolean };
  po_created: { estimate_id: string | number };
  po_auto_matched: { estimate_id: string | number };
  claim_raised: { estimate_id: string | number; basis?: 'percent' | 'amount' };
  contract_settled: { estimate_id: string | number; source?: string };

  // ── Money spine: P&L explain (WS6) ────────────────────────────────────────
  pnl_explain_opened: { number: 'contract' | 'spent' | 'to_spend' | 'forecast_profit' | 'committed' };
  pnl_changes_viewed: { estimate_id: string | number; days_since_snapshot?: number };

  // ── Money spine: labour budgeting (WS3) ───────────────────────────────────
  labour_budget_set: { estimate_id: string | number; category_count?: number };
  labour_split_suggested: { estimate_id: string | number; method: 'budget' | 'cost' };
  labour_allocation_saved: { estimate_id: string | number; fully_allocated: boolean; unallocated_bucket?: string };

  // ── Money spine: push engagement (WS5) ────────────────────────────────────
  spine_alert_clicked: { alert_key: string; days_since_fired?: number };
  weekly_digest_opened: { job_count?: number };
  money_alert_settings_changed: { alerts_enabled: boolean; digest_enabled: boolean };

  // ── Money spine: navigation (WS2) ─────────────────────────────────────────
  money_tab_viewed: { room: 'costings' | 'contract' | 'money'; estimate_id?: string | number };
  money_room_crosslink_clicked: { from: string; to: string };

  // ── Money spine: rate freshness (WS4c) ────────────────────────────────────
  // Fired when rate settings fall back to cache/defaults because the API failed.
  // In prod this is an ops alarm, not just a UX signal.
  rates_stale_fallback: { source: 'cache' | 'default'; age_days?: number };

  // ── AI assistant & command bar ──────────────────────────────────────────
  ai_chat_opened: { source?: 'bubble' | 'mat_ai_page' };
  ai_chat_message_sent: { has_image: boolean; page_context?: string; message_length: number };
  mat_ai_query: { has_job_selected: boolean; query_length: number };
  command_bar_used: { query_length: number };
  smart_notification_clicked: { notification_type: string; entity?: string };
}

export type AnalyticsEventName = keyof AnalyticsEvents;
export type AnalyticsEventProps<T extends AnalyticsEventName> = AnalyticsEvents[T];

/**
 * Dev-only guard (WS10): raw dollar amounts must never leave the browser — money
 * events ship bands/buckets/counts, not `$47,252.80` (privacy + better cohorting).
 * The typed AnalyticsEvents interface already prevents this for defined events;
 * this catches raw/ad-hoc calls in development. Keys ending _band/_bucket/_count/
 * _pct/_bps or _bucket are allowed; a numeric value on a $-looking key warns.
 */
const RAW_MONEY_KEY = /(^|_)(amount|value|cost|total|price|dollars|spent|budget|profit|contract|invoiced|paid)$/i;
function warnIfRawMoney(eventName: string, properties: Record<string, any>): void {
  if (!import.meta?.env?.DEV) return;
  for (const [k, v] of Object.entries(properties || {})) {
    if (typeof v === 'number' && RAW_MONEY_KEY.test(k)) {
      console.warn(
        `[analytics] "${eventName}" prop "${k}" looks like a raw dollar amount. ` +
        `Money events must ship bands/buckets/counts (e.g. ${k}_band, ${k}_bucket), not $ values.`
      );
    }
  }
}

/**
 * Composable wrapping PostHog with type-safe event tracking and graceful
 * fallbacks when PostHog isn't initialised (e.g. local dev).
 */
export function useAnalytics() {
  /**
   * Track a typed business event. Always safe to call — silently no-ops if
   * PostHog isn't loaded.
   */
  function track<T extends AnalyticsEventName>(
    eventName: T,
    properties: AnalyticsEventProps<T> = {} as AnalyticsEventProps<T>
  ): void {
    warnIfRawMoney(eventName as string, properties as Record<string, any>);
    try {
      if (typeof posthog?.capture !== 'function') return;
      posthog.capture(eventName, properties as Record<string, any>);
    } catch (e) {
      // Never let analytics break the app
      console.warn('[analytics] track failed:', e);
    }

    // Fire a milestone celebration if this is a first_* activation event.
    // Idempotent (gated by localStorage in useEntityCelebration), so safe to
    // call even if the event accidentally fires twice.
    const milestoneKind = FIRST_EVENT_TO_MILESTONE[eventName as string];
    if (milestoneKind) {
      try {
        useEntityCelebration().celebrate(milestoneKind);
      } catch {
        // Celebration is best-effort; never break tracking
      }
    }
  }

  /**
   * Track a `first_*` activation milestone, at most once per browser.
   *
   * The activation funnel is only meaningful if these fire exactly once —
   * `first_estimate_created` sent on every estimate would report an
   * activation rate near 100% and make onboarding look flawless no matter
   * how bad it is. Call sites therefore fire the plain event unconditionally
   * and this one alongside it; the guard lives here rather than being
   * re-implemented (and eventually got wrong) at a dozen call sites.
   *
   * Gated on localStorage, mirroring `useEntityCelebration`. That means a new
   * browser or a cleared profile can re-fire it for an existing user, so
   * treat the activation rate as a close estimate rather than an exact count.
   * A server-side flag on the user record would be exact; this deliberately
   * stays client-side to avoid a backend change.
   */
  function trackFirst<T extends AnalyticsEventName>(
    eventName: T,
    properties: AnalyticsEventProps<T> = {} as AnalyticsEventProps<T>
  ): void {
    try {
      const flagKey = `bs_first_event_${eventName}`;
      if (localStorage.getItem(flagKey)) return;
      localStorage.setItem(flagKey, String(Date.now()));
    } catch {
      // Private mode / storage disabled — fall through and track anyway.
      // An over-counted milestone beats a silently missing one.
    }
    track(eventName, properties);
  }

  /**
   * Add or update properties on the currently identified user.
   * Use this when something about the user changes mid-session
   * (e.g. they upgrade their plan).
   */
  function setUserProperties(properties: Record<string, any>): void {
    try {
      if (typeof posthog?.setPersonProperties !== 'function') return;
      posthog.setPersonProperties(properties);
    } catch (e) {
      console.warn('[analytics] setUserProperties failed:', e);
    }
  }

  /**
   * Add or update properties on the user's company / group, so we can build
   * company-level dashboards (e.g. "MRR by company size").
   */
  function setGroup(groupType: string, groupKey: string, properties: Record<string, any> = {}): void {
    try {
      if (typeof posthog?.group !== 'function') return;
      posthog.group(groupType, groupKey, properties);
    } catch (e) {
      console.warn('[analytics] setGroup failed:', e);
    }
  }

  /**
   * Check if a feature flag is enabled for the current user.
   * Use this to gate features behind PostHog feature flags.
   */
  function isFeatureEnabled(flagKey: string): boolean {
    try {
      if (typeof posthog?.isFeatureEnabled !== 'function') return false;
      return posthog.isFeatureEnabled(flagKey) === true;
    } catch (e) {
      return false;
    }
  }

  return {
    track,
    trackFirst,
    setUserProperties,
    setGroup,
    isFeatureEnabled,
  };
}
